import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ShoppingCart, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function WishlistPage() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    const fetch = async () => {
      try {
        const { data } = await api.get('/api/wishlist');
        setItems(data);
      } catch { /* empty */ }
      finally { setLoading(false); }
    };
    fetch();
  }, [user, api, navigate]);

  const removeItem = async (productId) => {
    try {
      await api.delete(`/api/wishlist/${productId}`);
      setItems(items.filter(i => i.product_id !== productId));
      toast.success('Removed from wishlist');
    } catch {
      toast.error('Failed to remove');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="wishlist-page">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-8">
          <Heart className="h-7 w-7 text-coral" />
          <h1 className="text-3xl font-heading font-bold text-navy">My Wishlist</h1>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner"></div></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="h-16 w-16 text-gray-200 mx-auto mb-4" />
            <h2 className="text-xl font-heading font-semibold text-navy mb-2">Your wishlist is empty</h2>
            <p className="text-gray-500 mb-6">Browse our products and save your favorites!</p>
            <Link to="/products">
              <Button className="bg-mint hover:bg-mint-dark text-black rounded-full">
                Browse Products
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {items.map((item, i) => item.product && (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl border border-gray-200 overflow-hidden group"
              >
                <Link to={`/products/${item.product_id}`}>
                  <div className="aspect-square bg-gray-100 overflow-hidden">
                    <img
                      src={item.product.image_url}
                      alt={item.product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </Link>
                <div className="p-4">
                  <Link to={`/products/${item.product_id}`}>
                    <h3 className="font-medium text-navy hover:text-mint transition-colors">{item.product.name}</h3>
                  </Link>
                  <p className="text-xs text-gray-500 mt-0.5">{item.product.category}</p>
                  <p className="text-lg font-bold text-navy mt-2">${item.product.base_price?.toFixed(2)}</p>
                  <div className="flex gap-2 mt-3">
                    <Link to={`/products/${item.product_id}`} className="flex-1">
                      <Button size="sm" className="w-full bg-mint hover:bg-mint-dark text-black rounded-full" data-testid={`wishlist-view-${item.product_id?.slice(0,8)}`}>
                        <ShoppingCart className="h-3.5 w-3.5 mr-1" /> View
                      </Button>
                    </Link>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-500 hover:bg-red-50"
                      onClick={() => removeItem(item.product_id)}
                      data-testid={`wishlist-remove-${item.product_id?.slice(0,8)}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
